// AI Content Studio — the keyword → draft → audit → publish workflow. AI calls
// hit the server stub today (deterministic templates); the same flow runs live
// once a provider key is connected in Integrations. The final step creates a real
// article and publishes it via seoApi.
import { useEffect, useState } from 'react';
import { seoApi, SeoArticle, SeoBlock, SeoCompetitor, SeoGenResult } from '../../../lib/adminApi';
import { ApiError } from '../../../lib/api';
import { ScoreRing, SparkIcon } from './seoShared';
import { AuditOverlay, AuditCheck, PreviewResult } from './SeoEditor';

function isExpired(e: unknown): boolean { return !!e && typeof e === 'object' && (e as ApiError).status === 403; }

type GenMode = 'openai' | 'claude' | 'multi';
const WF_LABELS = ['Keyword', 'Competitors', 'Outline', 'Review', 'Article', 'SEO Audit'];
const STEP_TITLES = ['Keyword & brief', 'Select competitors', 'Generate outline', 'Review outline', 'Generate article', 'SEO audit', 'Publish'];
const STEP_DESCS = [
  'Set the target keyword, page type and search intent.',
  'Choose which ranking pages to benchmark against.',
  'AI drafts an H1–H3 structure from the SERP + competitor scan.',
  'Approve or tweak the proposed outline before writing.',
  'Draft the full article from the approved outline.',
  'Score the draft and surface fixes before publishing.',
  'Choose publishing and indexing options, then ship.',
];
const NEXT_LABELS = ['Select competitors →', 'Generate outline →', 'Review outline →', 'Generate article →', 'Run SEO audit →', 'Continue →', 'Publish article'];
// Page type shapes HOW the AI writes the article — shown as a hint so it isn't a mystery.
const PAGE_TYPE_HINT: Record<string, string> = {
  'Blog article': 'Informational how-to / guide — ranks for "how do I…" searches and funnels readers to the tools.',
  'Landing page': 'Conversion page that sells one tool (hero, benefits, comparison, CTA) — like /youtube-stem-splitter.',
  'Comparison': 'A "Riffloop vs X" page targeting comparison searches.',
};

export function SeoAiStudio({ competitors, initialKeyword, onPublished, onExpire, toast }:
  { competitors: SeoCompetitor[]; initialKeyword?: string; onPublished: (a: SeoArticle) => void; onExpire: () => void; toast: (m: string) => void }) {
  const [mode, setMode] = useState<GenMode>('multi');
  const [step, setStep] = useState(1);
  const [keyword, setKeyword] = useState(initialKeyword || 'youtube stem splitter');
  const [kwOptions, setKwOptions] = useState<string[]>([]);
  useEffect(() => { seoApi.keywords().then((r) => setKwOptions(r.keywords.map((k) => k.term))).catch(() => {}); }, []);
  const [pageType, setPageType] = useState('Blog article');
  const [intent, setIntent] = useState('Informational');
  const [picked, setPicked] = useState<Record<string, boolean>>({ Moises: true, StemSplit: true, Transpose: true });
  const [outline, setOutline] = useState<SeoGenResult['outline']>([]);
  const [content, setContent] = useState<SeoBlock[]>([]);
  const [words, setWords] = useState(0);
  const [audit, setAudit] = useState<{ score: number; checks: AuditCheck[] } | null>(null);
  const [auditOpen, setAuditOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const compList = competitors;

  async function gen(kind: 'outline' | 'article' | 'audit') {
    return seoApi.generate({ kind, keyword, mode, page_type: pageType, intent });
  }

  async function next() {
    if (busy) return;
    setBusy(true);
    try {
      if (step === 7) { await doPublish(); return; }
      if (step === 2) { const r = await gen('outline'); setOutline(r.outline || []); }
      if (step === 4) { const r = await gen('article'); setContent(r.content || []); setWords(r.words || 0); }
      if (step === 5) { const r = await seoApi.generate({ kind: 'audit', keyword, mode, content }); setAudit({ score: r.score || 0, checks: (r.checks || []) as AuditCheck[] }); }
      setStep((s) => Math.min(7, s + 1));
    } catch (e) { if (isExpired(e)) onExpire(); else toast('Generation failed'); }
    finally { setBusy(false); }
  }

  async function doPublish() {
    try {
      const cap = keyword.replace(/\b\w/g, (c) => c.toUpperCase());
      const meta = await seoApi.generate({ kind: 'meta', keyword });
      const created = await seoApi.createArticle({
        title: cap, keyword, page_type: pageType, intent, gen_mode: mode,
        content: content.length ? content : undefined,
        meta_title: meta.meta_title, meta_description: meta.meta_description,
        secondary_keywords: [],
      });
      const pub = await seoApi.publishArticle(created.article.id);
      toast('Published — ' + pub.published_url);
      onPublished(pub.article);
      // reset for the next run
      setStep(1); setOutline([]); setContent([]); setWords(0); setAudit(null);
    } catch (e) { if (isExpired(e)) onExpire(); else toast('Publish failed'); }
  }

  async function applyFix(check: AuditCheck) {
    try {
      const r = await seoApi.generate({ kind: 'apply_fix', keyword, content, fix: { label: check.label, issue: check.issue, rec: check.rec } });
      if (r.content && r.content.length) {
        setContent(r.content); setWords(r.words || 0);
        const au = await seoApi.generate({ kind: 'audit', keyword, content: r.content });
        if (au.checks) setAudit({ score: au.score || 0, checks: au.checks as AuditCheck[] });
        toast('Fix applied');
      } else { toast('No change suggested'); }
    } catch (e) { if (isExpired(e)) onExpire(); else toast('Could not apply fix'); }
  }
  async function applyAll() {
    if (!audit) return;
    const issues = audit.checks.filter((c) => c.status === 'issue');
    if (!issues.length) return;
    try {
      const r = await seoApi.generate({ kind: 'apply_fix', keyword, content, fixes: issues.map((c) => ({ label: c.label, issue: c.issue, rec: c.rec })) });
      if (r.content && r.content.length) {
        setContent(r.content); setWords(r.words || 0);
        const au = await seoApi.generate({ kind: 'audit', keyword, content: r.content });
        if (au.checks) setAudit({ score: au.score || 0, checks: au.checks as AuditCheck[] });
        toast(`Applied ${issues.length} fixes`);
      } else { toast('No changes suggested'); }
    } catch (e) { if (isExpired(e)) onExpire(); else toast('Could not apply fixes'); }
  }
  // Preview a fix without committing → before/after diff in the overlay.
  async function previewFix(check: AuditCheck): Promise<PreviewResult | null> {
    try {
      const r = await seoApi.generate({ kind: 'apply_fix', keyword, content, fix: { label: check.label, issue: check.issue, rec: check.rec } });
      if (r.content && r.content.length) return { content: r.content, meta_title: r.meta_title, meta_description: r.meta_description, words: r.words };
      toast('No change suggested'); return null;
    } catch (e) { if (isExpired(e)) onExpire(); else toast('Could not preview fix'); return null; }
  }
  async function commitPreview(res: PreviewResult) {
    if (!res.content || !res.content.length) return;
    setContent(res.content); setWords(res.words || 0);
    try {
      const au = await seoApi.generate({ kind: 'audit', keyword, content: res.content });
      if (au.checks) setAudit({ score: au.score || 0, checks: au.checks as AuditCheck[] });
    } catch (e) { if (isExpired(e)) onExpire(); }
    toast('Change applied');
  }

  function stepStyle(n: number) {
    if (n < step) return { bg: 'rgba(34,211,176,.16)', bd: 'rgba(34,211,176,.5)', fg: '#22d3b0', labelFg: 'var(--body)', mark: '✓', connector: '#22d3b0' };
    if (n === step) return { bg: 'var(--vl-bg)', bd: 'var(--vl)', fg: 'var(--vl)', labelFg: 'var(--ink)', mark: String(n), connector: 'var(--bd)' };
    return { bg: 'var(--panel2)', bd: 'var(--bd)', fg: 'var(--soft)', labelFg: 'var(--soft)', mark: String(n), connector: 'var(--bd)' };
  }

  const C_ = { bg: 'rgba(217,119,87,.16)', bd: 'rgba(217,119,87,.4)', fg: '#d97757', tag: 'C' };
  const O_ = { bg: 'rgba(34,211,176,.16)', bd: 'rgba(34,211,176,.4)', fg: '#22d3b0', tag: 'O' };
  const M_ = { bg: 'var(--vl-bg)', bd: 'var(--vl)', fg: 'var(--vl)', tag: '⇄' };
  const pipelines: Record<GenMode, { label: string; steps: { title: string; sub: string; bg: string; bd: string; fg: string; tag: string }[] }> = {
    multi: { label: 'MULTI-AI PIPELINE', steps: [
      { ...C_, title: 'Claude', sub: 'keyword analysis · intent · structure' },
      { ...O_, title: 'OpenAI', sub: 'SEO analysis · missing kw · FAQ · gaps' },
      { ...M_, title: 'Merged outline', sub: 'best of both' },
      { ...C_, title: 'Claude', sub: 'final article generation' },
      { ...O_, title: 'OpenAI', sub: 'SEO audit · meta · titles' },
    ] },
    claude: { label: 'CLAUDE PIPELINE', steps: [
      { ...C_, title: 'Claude', sub: 'keyword analysis · intent' },
      { ...C_, title: 'Claude', sub: 'outline + structure' },
      { ...C_, title: 'Claude', sub: 'full article generation' },
      { ...C_, title: 'Claude', sub: 'self SEO review' },
    ] },
    openai: { label: 'OPENAI PIPELINE', steps: [
      { ...O_, title: 'OpenAI', sub: 'keyword + intent analysis' },
      { ...O_, title: 'OpenAI', sub: 'outline + FAQ' },
      { ...O_, title: 'OpenAI', sub: 'full article generation' },
      { ...O_, title: 'OpenAI', sub: 'SEO audit · meta · titles' },
    ] },
  };
  const pipe = pipelines[mode];

  const modes: { key: GenMode; name: string; sub: string; rec?: boolean }[] = [
    { key: 'openai', name: 'OpenAI', sub: 'audits · metadata' },
    { key: 'claude', name: 'Claude', sub: 'long-form content' },
    { key: 'multi', name: 'Multi-AI', sub: 'Claude + OpenAI', rec: true },
  ];

  return (
    <section>
      {/* banner */}
      <div style={{ background: 'linear-gradient(135deg, rgba(124,92,255,.12), rgba(177,75,240,.06))', border: '1px solid var(--vl-bg)', borderRadius: 18, padding: 24, marginBottom: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <div className="seo-pulse" style={{ width: 34, height: 34, borderRadius: 10, background: 'var(--vl-grad)', display: 'grid', placeItems: 'center' }}><SparkIcon size={18} /></div>
          <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--ink)' }}>AI Content Studio</div>
          <span className="seo-mono" style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.06em', color: 'var(--vl)', background: 'var(--vl-bg)', padding: '3px 8px', borderRadius: 6 }}>BETA</span>
        </div>
        <p style={{ margin: '0 0 0 44px', fontSize: 14, color: 'var(--body)' }}>Research, draft, and audit a full SEO article from a single keyword — grounded in your competitor library.</p>
      </div>

      {/* generation mode */}
      <div className="seo-card" style={{ padding: 20, marginBottom: 16 }}>
        <div className="seo-eyebrow" style={{ marginBottom: 14 }}>GENERATION MODE</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
          {modes.map((m) => (
            <button key={m.key} className={`seo-radio ${mode === m.key ? 'on' : ''}`} onClick={() => setMode(m.key)}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                <span className="ring"><i /></span>
                <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)' }}>{m.name}</span>
                {m.rec && <span className="seo-mono" style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.05em', color: '#22d3b0', background: 'rgba(34,211,176,.14)', padding: '2px 6px', borderRadius: 5 }}>REC</span>}
              </div>
              <div className="seo-mono" style={{ fontSize: 12, color: 'var(--soft)', paddingLeft: 24, marginTop: 5 }}>{m.sub}</div>
            </button>
          ))}
        </div>
      </div>

      {/* stepper */}
      <div className="seo-card" style={{ padding: 22, marginBottom: 16, overflowX: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minWidth: 640 }}>
          {WF_LABELS.map((l, i) => {
            const s = stepStyle(i + 1);
            return (
              <div key={l} style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, flex: 'none' }}>
                  <div className="seo-mono" style={{ width: 38, height: 38, borderRadius: 11, background: s.bg, border: `1.5px solid ${s.bd}`, display: 'grid', placeItems: 'center', color: s.fg, fontSize: 15, fontWeight: 700 }}>{s.mark}</div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: s.labelFg, whiteSpace: 'nowrap' }}>{l}</div>
                </div>
                <div style={{ flex: 1, height: 2, margin: '0 6px 22px', background: s.connector, minWidth: 8 }} />
              </div>
            );
          })}
          {(() => { const s = stepStyle(7); return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, flex: 'none' }}>
              <div style={{ width: 38, height: 38, borderRadius: 11, background: s.bg, border: `1.5px solid ${s.bd}`, display: 'grid', placeItems: 'center', color: s.fg, fontSize: 15 }}>🚀</div>
              <div style={{ fontSize: 11, fontWeight: 600, color: s.labelFg }}>Publish</div>
            </div>
          ); })()}
        </div>
      </div>

      {/* current step + pipeline */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.15fr 0.85fr', gap: 16, marginBottom: 16 }} className="seo-grid-2">
        <div className="seo-card" style={{ padding: 22, display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <span className="seo-mono" style={{ fontSize: 12, fontWeight: 700, color: 'var(--vl)' }}>STEP {step}/7</span>
            <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--ink)' }}>{STEP_TITLES[step - 1]}</span>
          </div>
          <p style={{ margin: '0 0 18px', fontSize: 14, color: 'var(--body)' }}>{STEP_DESCS[step - 1]}</p>

          {step === 1 && (
            <>
              <label style={{ display: 'block', marginBottom: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--body)', marginBottom: 7 }}>Target keyword <span style={{ color: 'var(--soft)', fontWeight: 400 }}>— type or pick from your researched keywords</span></div>
                <input className="seo-input seo-mono" list="seo-ai-kw" placeholder="e.g. youtube stem splitter" value={keyword} onChange={(e) => setKeyword(e.target.value)} />
                <datalist id="seo-ai-kw">{kwOptions.map((t) => <option key={t} value={t} />)}</datalist>
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <label><div style={{ fontSize: 12, fontWeight: 600, color: 'var(--body)', marginBottom: 7 }}>Page type</div>
                  <select className="seo-select" style={{ width: '100%' }} value={pageType} onChange={(e) => setPageType(e.target.value)}><option>Blog article</option><option>Landing page</option><option>Comparison</option></select>
                  <div style={{ fontSize: 11, color: 'var(--soft)', marginTop: 6, lineHeight: 1.4 }}>{PAGE_TYPE_HINT[pageType]}</div></label>
                <label><div style={{ fontSize: 12, fontWeight: 600, color: 'var(--body)', marginBottom: 7 }}>Search intent</div>
                  <select className="seo-select" style={{ width: '100%' }} value={intent} onChange={(e) => setIntent(e.target.value)}><option>Transactional</option><option>Commercial</option><option>Informational</option></select></label>
              </div>
            </>
          )}
          {step === 2 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
              {compList.length === 0 && (
                <div className="seo-secnote">No competitors yet — add them in the Competitor Library to benchmark against (optional; you can continue without).</div>
              )}
              {compList.slice(0, 6).map((c) => {
                const on = !!picked[c.name];
                return (
                  <label key={c.name} style={{ display: 'flex', alignItems: 'center', gap: 11, background: 'var(--field)', border: '1px solid var(--bd2)', borderRadius: 10, padding: '11px 13px', cursor: 'pointer' }} onClick={() => setPicked((p) => ({ ...p, [c.name]: !on }))}>
                    <span style={{ width: 18, height: 18, borderRadius: 5, background: on ? 'var(--vl)' : 'transparent', border: `1.5px solid ${on ? 'var(--vl)' : 'var(--bd2)'}`, display: 'grid', placeItems: 'center', color: '#fff', fontSize: 11 }}>{on ? '✓' : ''}</span>
                    <span style={{ flex: 1, fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>{c.name}</span>
                    <span className="seo-mono" style={{ fontSize: 12, color: 'var(--soft)' }}>{c.keywords} kw</span>
                  </label>
                );
              })}
            </div>
          )}
          {(step === 3 || step === 4) && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 14 }}>
              {(outline && outline.length ? outline : [{ level: 'H1', text: 'Generating…' }]).map((o, i) => (
                <div key={i} style={{ display: 'flex', gap: 10, padding: o.level === 'H1' ? '11px 13px' : '9px 13px 9px 28px', background: o.level === 'H1' ? 'var(--field)' : 'transparent', border: o.level === 'H1' ? '1px solid var(--bd2)' : 'none', borderRadius: 10, color: o.level === 'H1' ? 'var(--ink)' : 'var(--body)' }}>
                  <span className="seo-mono" style={{ fontWeight: 700, color: o.level === 'H1' ? 'var(--vl)' : o.level === 'FAQ' ? 'var(--soft)' : '#37a7ff' }}>{o.level}</span>
                  <span style={{ fontWeight: o.level === 'H1' ? 600 : 400 }}>{o.text}{o.tag && <span className="seo-mono" style={{ fontSize: 11, color: '#22d3b0', marginLeft: 8 }}>+ {o.tag}</span>}</span>
                </div>
              ))}
            </div>
          )}
          {step === 5 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ height: 9, borderRadius: 6, background: 'var(--bd2)', overflow: 'hidden' }}><div style={{ width: '100%', height: '100%', background: 'linear-gradient(90deg,#d97757,#7c5cff,#22d3b0)' }} /></div>
              <div className="seo-mono" style={{ fontSize: 13, color: 'var(--body)' }}>{words || '—'} words · {content.length} blocks</div>
              <div style={{ background: 'var(--field)', border: '1px solid var(--bd2)', borderRadius: 10, padding: 14, fontSize: 14, lineHeight: 1.6, color: 'var(--body)' }}>
                {(content.find((b) => b.type === 'paragraph')?.text as string) || 'Draft preview will appear here.'}
              </div>
            </div>
          )}
          {step === 6 && audit && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 14 }}>
              <ScoreRing score={audit.score} size={64} stroke={4} />
              <div style={{ fontSize: 14, color: 'var(--body)' }}>Draft scores <b style={{ color: '#22d3b0' }}>{audit.score}/100</b>. {audit.checks.filter((c) => c.status === 'issue').length} fixes flagged before publishing.</div>
            </div>
          )}
          {step === 6 && audit && <button className="seo-btn-soft" onClick={() => setAuditOpen(true)}>◎ View full audit</button>}
          {step === 7 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {['Submit to Google Index on publish', 'Rebuild sitemap.xml', 'Schedule for later'].map((t, i) => (
                <label key={t} style={{ display: 'flex', alignItems: 'center', gap: 11, fontSize: 14, color: 'var(--body)' }}>
                  <span style={{ width: 18, height: 18, borderRadius: 5, background: i < 2 ? 'var(--vl)' : 'var(--bd2)', display: 'grid', placeItems: 'center', color: '#fff', fontSize: 11 }}>{i < 2 ? '✓' : ''}</span>{t}
                </label>
              ))}
              <div className="seo-secnote" style={{ marginTop: 6 }}>Publishing saves &amp; renders the page in the admin. Going live on riffloop.app is a separate step.</div>
            </div>
          )}

          <div style={{ flex: 1, minHeight: 14 }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 18 }}>
            <button className="seo-btn" disabled={step === 1 || busy} onClick={() => setStep((s) => Math.max(1, s - 1))}>← Back</button>
            <div style={{ flex: 1 }} />
            <button className="seo-btn-grad" disabled={busy} onClick={next}>{busy ? 'Working…' : NEXT_LABELS[step - 1]}</button>
          </div>
        </div>

        {/* pipeline */}
        <div className="seo-card" style={{ padding: 22 }}>
          <div className="seo-eyebrow" style={{ marginBottom: 16 }}>{pipe.label}</div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {pipe.steps.map((p, i) => (
              <div key={i} style={{ display: 'flex', gap: 12 }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 'none' }}>
                  <div className="seo-mono" style={{ width: 30, height: 30, borderRadius: 8, background: p.bg, border: `1px solid ${p.bd}`, display: 'grid', placeItems: 'center', color: p.fg, fontSize: 11, fontWeight: 700 }}>{p.tag}</div>
                  <div style={{ width: 2, flex: 1, minHeight: 14, background: 'var(--bd)' }} />
                </div>
                <div style={{ paddingBottom: 14 }}><div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>{p.title}</div><div className="seo-mono" style={{ fontSize: 12, color: 'var(--soft)', marginTop: 1 }}>{p.sub}</div></div>
              </div>
            ))}
            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ width: 30, height: 30, flex: 'none', borderRadius: 8, background: 'rgba(34,211,176,.16)', border: '1px solid rgba(34,211,176,.4)', display: 'grid', placeItems: 'center', color: '#22d3b0', fontSize: 13 }}>★</div>
              <div><div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>Final article</div><div className="seo-mono" style={{ fontSize: 12, color: 'var(--soft)', marginTop: 1 }}>merged + audited</div></div>
            </div>
          </div>
        </div>
      </div>

      {/* strengths */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14 }} className="seo-grid-3">
        <div className="seo-card" style={{ padding: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 11 }}><span style={{ width: 8, height: 8, borderRadius: 50, background: '#22d3b0' }} /><span style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)' }}>OpenAI strengths</span></div>
          <div style={{ fontSize: 13, lineHeight: 1.9, color: 'var(--body)' }}>SEO optimization<br />Metadata generation<br />FAQ generation<br />Keyword coverage</div>
        </div>
        <div className="seo-card" style={{ padding: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 11 }}><span style={{ width: 8, height: 8, borderRadius: 50, background: '#d97757' }} /><span style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)' }}>Claude strengths</span></div>
          <div style={{ fontSize: 13, lineHeight: 1.9, color: 'var(--body)' }}>Long-form writing<br />Structure<br />Readability<br />Content flow</div>
        </div>
        <div style={{ background: 'linear-gradient(135deg,rgba(124,92,255,.12),rgba(34,211,176,.06))', border: '1px solid var(--vl-bg)', borderRadius: 14, padding: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 11 }}><span style={{ fontSize: 13 }}>✦</span><span style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)' }}>Multi-AI</span></div>
          <div style={{ fontSize: 13, lineHeight: 1.55, color: 'var(--body)' }}>Combines both approaches for higher quality. Recommended for important SEO pages.</div>
        </div>
      </div>

      {auditOpen && audit && <AuditOverlay score={audit.score} checks={audit.checks} onClose={() => setAuditOpen(false)} onApplyFix={applyFix} onApplyAll={applyAll}
        current={{ content, meta_title: '', meta_description: '' }} onPreviewFix={previewFix} onCommit={commitPreview} />}
    </section>
  );
}
